import { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

const load = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; }
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => load("shodan_cart"));
  const [wishlist, setWishlist] = useState(() => load("shodan_wishlist"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const syncTimer = useRef(null);

  useEffect(() => { localStorage.setItem("shodan_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem("shodan_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  useEffect(() => {
    const email = user?.email || localStorage.getItem("shodan_email");
    if (!email) return;
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      api.post("/cart/snapshot", {
        email,
        items: items.map((i) => ({ item_type: i.item_type, slug: i.slug, name: i.name, price: i.price, quantity: i.quantity, image: i.image || null, sku_code: i.sku_code || null, variant_label: i.variant_label || null, component_skus: i.component_skus || null })),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(syncTimer.current);
  }, [items, user]);

  const restoreItems = (list) => {
    setItems((prev) => {
      const next = [...prev];
      for (const entry of list) {
        const idx = next.findIndex((i) => i.slug === entry.slug && i.item_type === entry.item_type && (i.sku_code || "") === (entry.sku_code || ""));
        if (idx === -1) next.push(entry);
      }
      return next;
    });
  };

  const addItem = (entry, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.slug === entry.slug && i.item_type === entry.item_type && (i.sku_code || "") === (entry.sku_code || ""));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, { ...entry, quantity: qty }];
    });
    toast.success(`${entry.name} added to cart`);
  };

  const updateQty = (slug, item_type, quantity, sku_code = "") => {
    setItems((prev) => quantity <= 0 ? prev.filter((i) => !(i.slug === slug && i.item_type === item_type && (i.sku_code || "") === sku_code))
      : prev.map((i) => (i.slug === slug && i.item_type === item_type && (i.sku_code || "") === sku_code ? { ...i, quantity } : i)));
  };

  const removeItem = (slug, item_type, sku_code = "") => setItems((prev) => prev.filter((i) => !(i.slug === slug && i.item_type === item_type && (i.sku_code || "") === sku_code)));
  const clearCart = () => setItems([]);

  const toggleWishlist = (slug) => {
    setWishlist((prev) => {
      if (prev.includes(slug)) { toast("Removed from wishlist"); return prev.filter((s) => s !== slug); }
      toast.success("Saved to wishlist");
      return [...prev, slug];
    });
  };

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  // all mutators use functional setState updates, so memoizing on state alone is safe
  const value = useMemo(() => ({ items, addItem, updateQty, removeItem, clearCart, restoreItems, subtotal, count, wishlist, toggleWishlist, drawerOpen, setDrawerOpen }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, wishlist, drawerOpen]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
